import React from 'react';
import TemporalChart, { type TemporalRow } from './TemporalChart';
import type { Site, SiteGeography, Activity } from '../../../../state/api/organisationEngineSlice';

type TimelineSectionProps = {
    site: Site;
    geographies: SiteGeography[];
    activities: Activity[];
    onBarClick?: (rowLabel: string, segmentIndex: number) => void;
    onAddClick?: (rowLabel: string) => void;
};

const toMs = (iso: string) => new Date(iso).getTime();

const TimelineSection = ({ site, geographies, activities, onBarClick, onAddClick }: TimelineSectionProps) => {
    const rows: TemporalRow[] = [];

    // Site row — always one segment, no add button
    rows.push({
        label: 'Site',
        showAddButton: false,
        segments: [
            {
                start: toMs(site.valid_from),
                end: site.valid_to ? toMs(site.valid_to) : null,
                color: 'bg-primary',
                tooltip: `${site.name} — ${site.status}`,
            },
        ],
    });

    // Geography row — one segment per historical version
    rows.push({
        label: 'Location',
        segments: geographies.map(geo => ({
            start: toMs(geo.properties.valid_from),
            end: geo.properties.valid_to ? toMs(geo.properties.valid_to) : null,
            color: 'bg-green-100',
            tooltip: `${geo.geometry.type}`,
        })),
    });

    // Activity row — one segment per historical version
    rows.push({
        label: 'Activity',
        segments: activities.map(act => ({
            start: toMs(act.valid_from),
            end: act.valid_to ? toMs(act.valid_to) : null,
            color: 'bg-orange-100',
            tooltip: `ISIC ${act.isic_code}${act.isic_description ? ` — ${act.isic_description}` : ''}`,
        })),
    });

    return <TemporalChart rows={rows} onBarClick={onBarClick} onAddClick={onAddClick} />;
};

export default TimelineSection;
